import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Edit02Icon,
  Delete02Icon,
  Calendar03Icon,
  UserIcon,
  AiChat02Icon,
  CoinsIcon,
} from "@hugeicons/core-free-icons";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { createProjectsApi } from "@/lib/api/projects";
import type { Project } from "@/lib/api/projects";
import { RenameProjectDialog } from "@/components/project/RenameProjectDialog";
import { DeleteProjectDialog } from "@/components/project/DeleteProjectDialog";
import { ModelCombobox } from "@/components/models/ModelCombobox";
import { useModelCatalogStore } from "@/lib/stores/model-catalog-store";
import logoLight from "@/assets/png/logo.png";

/* ──────────────────────────── Overview ──────────────────────────── */

function OverviewSection({ project }: { project: Project }) {
  const { user } = useUser();

  const createdDate = new Date(project.createdAt).toLocaleDateString(
    undefined,
    { year: "numeric", month: "long", day: "numeric" },
  );

  const items = [
    {
      icon: Edit02Icon,
      label: "Display name",
      value: project.name,
    },
    {
      icon: UserIcon,
      label: "Owner",
      value: user?.fullName ?? "You",
    },
    {
      icon: AiChat02Icon,
      label: "Messages",
      value: "0",
    },
    {
      icon: Calendar03Icon,
      label: "Created",
      value: createdDate,
    },
    {
      icon: CoinsIcon,
      label: "Credits consumed",
      value: "0",
    },
  ];

  return (
    <Card className="bg-transparent">
      <CardHeader>
        <CardTitle>Overview</CardTitle>
        <CardDescription>Key details about this project.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
          {items.map((item) => (
            <div key={item.label} className="flex items-center gap-3">
              <div className="bg-muted flex size-8 items-center justify-center rounded-lg">
                <HugeiconsIcon icon={item.icon} className="size-4" />
              </div>
              <div className="flex flex-1 flex-col">
                <span className="text-muted-foreground text-xs">
                  {item.label}
                </span>
                <span className="text-sm font-medium">{item.value}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────── Default Model Section ───────────────────── */

function DefaultModelSection({
  project,
  getToken,
}: {
  project: Project;
  getToken: () => Promise<string | null>;
}) {
  const queryClient = useQueryClient();
  const projectsApi = createProjectsApi({ getToken });
  const fetchModelCatalog = useModelCatalogStore((s) => s.fetch);

  useEffect(() => {
    fetchModelCatalog(getToken);
  }, [fetchModelCatalog, getToken]);

  const updateDefaultModelMutation = useMutation({
    mutationFn: (modelId: string) =>
      projectsApi.update(project.id, { agent_model: modelId }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["project", project.id], updated);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  return (
    <Card className="bg-transparent">
      <CardHeader>
        <CardTitle>Default model</CardTitle>
        <CardDescription>
          Used for this project&apos;s chat model and as the default for new runs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <ModelCombobox
          categories={["text"]}
          value={project.agentModel}
          disabled={updateDefaultModelMutation.isPending}
          onSelect={(modelId) => {
            if (modelId === project.agentModel) return;
            updateDefaultModelMutation.mutate(modelId);
          }}
        />
        {updateDefaultModelMutation.isPending && (
          <p className="text-xs text-muted-foreground">Saving default model...</p>
        )}
        {updateDefaultModelMutation.isError && (
          <p className="text-xs text-destructive">
            Could not save the default model.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/* ──────────────────────── Actions Section ───────────────────────── */

function ActionsSection({
  project,
  getToken,
}: {
  project: Project;
  getToken: () => Promise<string | null>;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [renameOpen, setRenameOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [includeHistory, setIncludeHistory] = useState(false);

  const projectsApi = createProjectsApi({ getToken });

  const cloneMutation = useMutation({
    mutationFn: () => projectsApi.clone(project.id),
    onSuccess: (cloned) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setCloneOpen(false);
      navigate(`/projects/${cloned.id}`);
    },
  });

  const starMutation = useMutation({
    mutationFn: () => projectsApi.star(project.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["project", project.id] });
    },
  });

  return (
    <>
      <Card className="bg-transparent py-0">
        <CardContent>
          <div className="flex items-center justify-between py-5">
            <div>
              <p className="text-sm font-medium">Rename project</p>
              <p className="text-muted-foreground text-sm">
                Update your project's title.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRenameOpen(true)}
            >
              Rename
            </Button>
          </div>
          <Separator />
          <div className="flex items-center justify-between py-5">
            <div>
              <p className="text-sm font-medium">Clone project</p>
              <p className="text-muted-foreground text-sm">
                Duplicate this project into a new copy.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCloneOpen(true)}
            >
              Clone
            </Button>
          </div>
          <Separator />
          <div className="flex items-center justify-between py-5">
            <div>
              <p className="text-sm font-medium">
                {project.isStarred ? "Unstar project" : "Star project"}
              </p>
              <p className="text-muted-foreground text-sm">
                {project.isStarred
                  ? "Remove this project from your starred list."
                  : "Add this project to your starred list for quick access."}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => starMutation.mutate()}
              disabled={starMutation.isPending}
            >
              {project.isStarred ? "Unstar" : "Star"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <RenameProjectDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        projectId={project.id}
        currentName={project.name}
        getToken={getToken}
      />

      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent className="sm:max-w-[500px] bg-surface-raised border-white/10 p-6">
          <div className="flex items-center gap-3 mb-2">
            <img
              src={logoLight}
              alt="Rayloop AI"
              className="size-12 rounded-lg invert dark:invert-0"
            />
          </div>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-surface-raised-text">
              Clone project
            </DialogTitle>
            <DialogDescription className="text-surface-raised-text-muted">
              By cloning a project, you will create a copy that you own.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center justify-between">
              <label
                htmlFor="include-history-settings"
                className="text-sm font-medium text-surface-raised-text cursor-pointer"
              >
                Include project history
              </label>
              <Switch
                id="include-history-settings"
                checked={includeHistory}
                onCheckedChange={setIncludeHistory}
              />
            </div>
          </div>
          <DialogFooter className="bg-transparent border-t-0 -mx-6 -mb-6 p-6 pt-4">
            <Button
              variant="outline"
              onClick={() => setCloneOpen(false)}
              disabled={cloneMutation.isPending}
              className="bg-transparent border-white/20 text-surface-raised-text hover:bg-surface-raised-hover"
            >
              Cancel
            </Button>
            <Button
              onClick={() => cloneMutation.mutate()}
              disabled={cloneMutation.isPending}
              className="bg-white text-black hover:bg-white/90"
            >
              {cloneMutation.isPending ? "Cloning…" : "Clone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ──────────────────── Danger Zone ───────────────────────────────── */

function DangerZoneSection({
  project,
  getToken,
}: {
  project: Project;
  getToken: () => Promise<string | null>;
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <>
      <Card className="bg-transparent">
        <CardHeader>
          <CardTitle>Danger zone</CardTitle>
          <CardDescription>
            Permanently delete this project. This cannot be undone.
          </CardDescription>
          <CardAction className="self-center">
            <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
              <HugeiconsIcon icon={Delete02Icon} className="size-4" />
              Delete project
            </Button>
          </CardAction>
        </CardHeader>
      </Card>

      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        projectId={project.id}
        projectName={project.name}
        getToken={getToken}
        navigateOnDelete
      />
    </>
  );
}

/* ──────────────────── Main Export ──────────────────────────────── */

export function ProjectSettings({ projectId }: { projectId: string }) {
  const { getToken } = useAuth();
  const projectsApi = createProjectsApi({ getToken });

  const { data: project, isLoading } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => projectsApi.get(projectId),
  });

  if (isLoading) {
    return (
      <div className="w-full space-y-6 p-8">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-4 w-64" />
        </div>
        {/* Overview skeleton */}
        <Card className="bg-transparent">
          <CardHeader>
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-x-8 gap-y-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="size-8 rounded-lg" />
                  <div className="flex flex-1 flex-col gap-1">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        {/* Actions skeleton */}
        <Card className="bg-transparent py-0">
          <CardContent>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i}>
                <div className="flex items-center justify-between py-5">
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-52" />
                  </div>
                  <Skeleton className="h-8 w-20 rounded-md" />
                </div>
                {i < 2 && <Separator />}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="w-full space-y-6 p-8">
        <div>
          <h1 className="text-xl font-semibold">Project Settings</h1>
          <p className="text-muted-foreground text-sm">Project not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 p-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-semibold">Project Settings</h1>
        <p className="text-muted-foreground text-sm">
          Manage settings for{" "}
          <span className="font-medium text-foreground">{project.name}</span>.
        </p>
      </div>
      <OverviewSection project={project} />
      <DefaultModelSection project={project} getToken={getToken} />
      <ActionsSection project={project} getToken={getToken} />
      <DangerZoneSection project={project} getToken={getToken} />
    </div>
  );
}
